import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { db } from "../utils/firebase";
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, collection as fbCollection, query, where, addDoc, getDocs } from "firebase/firestore";
import { Box, Typography, CircularProgress, Paper, Button, Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import EventSignupQuestions from "../components/EventSignupQuestions";

// --- Helper utilities ---
function formatDisplayName(profile) {
  if (!profile) return 'Participant';
  const rawName = profile.name || profile.displayName || profile.fullName || '';
  if (rawName.trim()) {
    const parts = rawName.trim().split(/\s+/);
    const first = parts[0];
    const lastInitial = parts.length > 1 ? parts[parts.length - 1][0] + '.' : '';
    return `${first} ${lastInitial}`.trim();
  }
  if (profile.email) return profile.email.split('@')[0];
  return 'Participant';
}

function computeAge(profile) {
  if (!profile) return null;
  const dobLike = profile.dob || profile.birthday || profile.birthdate || profile.dateOfBirth || profile?.profile?.dob || profile?.profile?.birthday;
  if (!dobLike) return null;
  let dateObj = null;
  if (typeof dobLike === 'object' && dobLike?.seconds) {
    dateObj = new Date(dobLike.seconds * 1000);
  } else {
    dateObj = new Date(dobLike);
  }
  if (isNaN(dateObj)) return null;
  const today = new Date();
  let age = today.getFullYear() - dateObj.getFullYear();
  const m = today.getMonth() - dateObj.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dateObj.getDate())) age--;
  if (age < 0 || age > 120) return null;
  return age;
}

function getOccupation(profile) {
  // Source occupation from the user's profile only (not event signup answers)
  return profile?.occupation || profile?.profile?.occupation || '';
}

function getProfileImage(profile) {
  return profile?.profilePicture || profile?.photoURL || profile?.profile?.photoURL || 'https://via.placeholder.com/120?text=User';
}

// Format US phone numbers to (XXX) XXX-XXXX; otherwise return original
function formatPhoneNumber(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  // Allow leading country code 1
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length === 10) {
    const area = d.slice(0, 3);
    const mid = d.slice(3, 6);
    const last = d.slice(6);
    return `(${area}) ${mid}-${last}`;
  }
  return String(value);
}

function getParticipantStatusLabel(status) {
  switch ((status || '').toLowerCase()) {
    case 'pending':
      return 'Pending Approval';
    case 'approved_pending_payment':
      return 'Approved - Pending Payment';
    case 'approved_paid':
      return 'Approved & Paid';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Signed Up';
  }
}

function MainEventPage() {
  const { eventId } = useParams();
  const { user, role } = useAuth();

  // Core state
  const [event, setEvent] = useState(null);
  const [mode, setMode] = useState('signups');
  const [signupLimit, setSignupLimit] = useState('');
  const [loading, setLoading] = useState(true);

  // Signup related
  const [profile, setProfile] = useState(null);
  const [myParticipant, setMyParticipant] = useState(null);
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState('');
  const [showSignup, setShowSignup] = useState(false);
  const [signupAnswers, setSignupAnswers] = useState({ agreementAccepted: false });
  const [signupTouched, setSignupTouched] = useState({});
  const signupErrors = React.useMemo(() => {
    const v = signupAnswers || {};
    const errs = {};
    const req = (k, msg='Required') => { const val = v[k]; if ((typeof val === 'string' && !val.trim()) || val === '' || val == null) errs[k] = msg; };
    req('within2Hours');
    req('whyAttend');
    req('jesusRelationship');
    req('nickname');
    req('legallySingle');
    req('church');
    req('personalityWords');
    req('hasCharges');
    req('backgroundInfo');
    req('paymentMethod');
    if (v.agreementAccepted !== true) errs.agreementAccepted = 'You must accept the agreement.';
    return errs;
  }, [signupAnswers]);
  const allSignupFieldsValid = React.useMemo(() => {
    const v = signupAnswers || {};
    const required = [
      'within2Hours',
      'whyAttend',
      'jesusRelationship',
      'nickname',
      'legallySingle',
      'church',
      'personalityWords',
      'hasCharges',
      'backgroundInfo',
      'paymentMethod',
    ];
    return v.agreementAccepted === true && required.every(k => {
      const val = v[k];
      return typeof val === 'string' ? val.trim().length > 0 : val != null && val !== '';
    });
  }, [signupAnswers]);

  // Matching related
  const [eventParticipants, setEventParticipants] = useState([]);
  const [participantProfiles, setParticipantProfiles] = useState([]);
  const [matchSelections, setMatchSelections] = useState([]);
  const [matchFormOpen, setMatchFormOpen] = useState(false);
  const [matchSubmitted, setMatchSubmitted] = useState(false);
  const [hasPreviousMatchForm, setHasPreviousMatchForm] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchFetchError, setMatchFetchError] = useState('');
  const [matchApiMeta, setMatchApiMeta] = useState(null);
  const matchFormRef = useRef(null);
  const urlWantsMatch = useRef(new URLSearchParams(window.location.search).get('openMatchForm') === '1');
  const openRetries = useRef(0);
  // diagnostics removed

  // Mutual matches (for finished events)
  const [mutualMatches, setMutualMatches] = useState([]);
  const [likedByOnly, setLikedByOnly] = useState([]);
  const [interestedInYou, setInterestedInYou] = useState([]);
  const [mutualLoading, setMutualLoading] = useState(false);
  const [mutualError, setMutualError] = useState('');
  const [lateMatchPrompt, setLateMatchPrompt] = useState('');
  const [severingIds, setSeveringIds] = useState([]);
  // Dialog state for sever match
  const [severDialogOpen, setSeverDialogOpen] = useState(false);
  const [severTarget, setSeverTarget] = useState(null); // { uid, name }
  const [severReason, setSeverReason] = useState('');
  const [severSubmitting, setSeverSubmitting] = useState(false);
  const [severSubmitError, setSeverSubmitError] = useState('');

  // Fetch event + participant (basic) info
  useEffect(() => {
    async function fetchEventAndProfile() {
      setLoading(true);
      try {
        // Fetch sanitized event from backend API
        if (user) {
          try {
            const token = await user.getIdToken();
            const baseUrl = (process.env.REACT_APP_BACKEND_URL || '');
            const resp = await fetch(`${baseUrl}/api/event/${encodeURIComponent(eventId)}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const json = await resp.json().catch(() => ({}));
            if (resp.ok && json.event) {
              setEvent(json.event);
              setMode(json.event.mode || 'signups');
              setSignupLimit(json.event.signupLimit || '');
            }
          } catch (_) { /* fallthrough */ }
        }
        // If no user (public view), do a minimal fetch from public list for this event id without specificLocation
        if (!user) {
          try {
            const baseUrl = (process.env.REACT_APP_BACKEND_URL || '');
            const resp = await fetch(`${baseUrl}/api/event/list-public`);
            const json = await resp.json().catch(() => ({}));
            if (resp.ok && Array.isArray(json.events)) {
              const found = json.events.find(e => e.id === eventId);
              if (found) {
                setEvent(found);
                setMode(found.mode || 'signups');
                setSignupLimit(found.signupLimit || '');
              }
            }
          } catch (_) { /* ignore */ }
        }
        let parts = [];
        if (user) {
          if (role === 'admin') {
            // Admin gets all participants
            const partsQ = query(fbCollection(db, 'participants'), where('eventId', '==', eventId));
            const snap = await getDocs(partsQ);
            parts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          } else {
            // Regular user only fetches their participant to reduce rule surface area
            const partsQ = query(
              fbCollection(db, 'participants'),
              where('eventId', '==', eventId),
              where('userId', '==', user.uid)
            );
            const snap = await getDocs(partsQ);
            parts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          }
        }
        setEventParticipants(parts);
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) setProfile(userDoc.data());
          const mine = parts.find(p => p.userId === user.uid) || null;
          setMyParticipant(mine);
        } else {
          setMyParticipant(null);
        }
      } finally {
        setLoading(false);
      }
    }
    if (eventId) fetchEventAndProfile();
  }, [eventId, user, role]);

  // Fallback: if user present, mode in-progress, query param wants match, but myParticipant still null, attempt single fetch again
  useEffect(() => {
    if (!user || !eventId) return;
    if (!urlWantsMatch.current) return;
    if (myParticipant) return;
    // attempt lightweight refetch of user's participant
    (async () => {
      try {
        const partsQ = query(
          fbCollection(db, 'participants'),
          where('eventId', '==', eventId),
          where('userId', '==', user.uid)
        );
        const snap = await getDocs(partsQ);
        const parts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (parts.length) {
          setEventParticipants(prev => prev.length ? prev : parts); // don't overwrite admin full list
          setMyParticipant(parts[0]);
        }
      } catch (e) {
        // silent
      }
    })();
  }, [user, eventId, myParticipant]);

  // Fetch mutual matches when event is finished
  useEffect(() => {
    (async () => {
      if (!eventId || !user) return;
      if (!event || event.mode !== 'finished') return;
      setMutualLoading(true);
      setMutualError('');
      try {
        const token = await user.getIdToken();
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
        const resp = await fetch(`${backendUrl}/api/match/event/${eventId}/mutual`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const reason = json?.meta?.reason || json?.message || `HTTP ${resp.status}`;
          setMutualMatches([]);
          setLikedByOnly([]);
          throw new Error(reason);
        }
        const arr = json.matches || json.participants || [];
        setMutualMatches(Array.isArray(arr) ? arr : []);
        const liked = json.likedByOnly || [];
        setLikedByOnly(Array.isArray(liked) ? liked : []);
        const interested = json.interestedInYou || liked;
        setInterestedInYou(Array.isArray(interested) ? interested : []);
      } catch (e) {
        setMutualError(e.message || 'Failed to load mutual matches');
      } finally {
        setMutualLoading(false);
      }
    })();
  }, [eventId, user, event]);

  const handleSeverMatch = async (otherUserId) => {
    if (!user || !eventId || !otherUserId) return;
    let reason = '';
    try {
      reason = window.prompt('Optional: provide a short reason (or leave blank)') || '';
    } catch (_) {
      reason = '';
    }
    setSeveringIds(prev => Array.from(new Set([...(prev||[]), otherUserId])));
    try {
      const token = await user.getIdToken();
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
      const resp = await fetch(`${backendUrl}/api/match/event/${eventId}/mutual/sever`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ otherUserId, reason })
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.message || json?.meta?.reason || `HTTP ${resp.status}`);
      // Optimistic update: mark this mutual as severed locally
      setMutualMatches(prev => (prev || []).map(p => {
        const id = p.uid || p.id;
        if (id === otherUserId) {
          return { ...p, severed: true, severedBy: user.uid, severedAt: new Date().toISOString(), severedReason: reason };
        }
        return p;
      }));
    } catch (e) {
      setMutualError(e.message || 'Failed to sever match');
    } finally {
      setSeveringIds(prev => (prev || []).filter(id => id !== otherUserId));
    }
  };

  // Open + populate match form
  const openMatchForm = async () => {
    if (!user || !eventId) return;
    if (!event || event.mode !== 'in-progress') return;
    setMatchLoading(true);
    setMatchFetchError('');
  // diagnostics removed
    try {
      // Admin: can still directly query (assumed allowed by rules)
      if (role === 'admin') {
        let parts = eventParticipants;
        if (parts.length <= 1) {
          const partsQ = query(fbCollection(db, 'participants'), where('eventId', '==', eventId));
          const snap = await getDocs(partsQ);
          parts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setEventParticipants(parts);
        }
        const filtered = parts.filter(p => p.status !== 'cancelled');
        const uids = filtered.map(p => p.userId);
        const docsArr = await Promise.all(uids.map(uid => getDoc(doc(db, 'users', uid))));
        // Attach status to profile objects for admin so we can filter to approved_paid client-side
        const statusByUid = new Map(filtered.map(p => [p.userId, p.status]));
        const profiles = docsArr
          .filter(d => d.exists())
          .map(d => ({ uid: d.id, status: statusByUid.get(d.id), ...d.data() }));
        setParticipantProfiles(profiles);
      } else {
        // Regular user: call backend secure endpoint
        const token = await user.getIdToken();
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
        const resp = await fetch(`${backendUrl}/api/match/event/${eventId}/participants`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await resp.json().catch(() => ({}));
        setMatchApiMeta(json.meta || null);
        if (!resp.ok) {
          setParticipantProfiles(json.participants || []);
          throw new Error(json.meta?.reason || json.message || `HTTP ${resp.status}`);
        } else {
          let profiles = json.participants || [];
          const metaSupportsStatus = !!(json.meta && (typeof json.meta.approvedPaidParticipants === 'number' || json.meta.statusCounts));
          const missingAnyStatus = profiles.some(p => p.status == null);
          // If backend doesn't provide statuses, resolve them via helper endpoint
          if (!metaSupportsStatus && missingAnyStatus && profiles.length) {
            try {
              const uids = profiles.map(p => p.uid || p.id).filter(Boolean);
              const rs = await fetch(`${backendUrl}/api/match/event/${eventId}/resolve-status`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userIds: uids })
              });
              const rsJson = await rs.json().catch(() => ({}));
              const statuses = rsJson.statuses || {};
              profiles = profiles.map(p => ({ ...p, status: statuses[p.uid || p.id] ?? p.status ?? null }));
              // Flag in meta for diagnostics
              setMatchApiMeta(prev => ({ ...(prev || {}), statusBackfilled: true }));
            } catch (e) {
              // swallow backfill failures; strict filter will yield none
            }
          }
          setParticipantProfiles(profiles);
          if (json.meta?.reason) setMatchFetchError(json.meta.reason);
        }
      }
                    if (matchFetchError) {
                      return <Alert severity="error" sx={{ mb: 2 }}>{matchFetchError}{matchApiMeta?.reason ? ` (${matchApiMeta.reason})` : ''}</Alert>;
                    }
      const existing = await getDoc(doc(db, `matches/${eventId}_${user.uid}`));
      if (existing.exists()) {
        const data = existing.data();
        if (Array.isArray(data.selections)) setMatchSelections(data.selections);
        setHasPreviousMatchForm(true);
      } else {
        setMatchSelections([]);
      }
      setMatchFormOpen(true);
      setTimeout(() => matchFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (e) {
  // silent
      setMatchFetchError(e.message || 'Failed to load matches');
    } finally {
      setMatchLoading(false);
  // diagnostics removed
    }
  };

  // Auto-open when query param present
  useEffect(() => {
    if (!urlWantsMatch.current) return;
    // Only in-progress events should auto-open the match form.
    if (!event || event.mode !== 'in-progress') {
      setMatchFormOpen(false);
      urlWantsMatch.current = false;
      return;
    }
    if (!matchFormOpen) setMatchFormOpen(true);
    if (myParticipant && myParticipant.status !== 'cancelled') {
      openMatchForm();
    } else if (openRetries.current < 8) {
      openRetries.current += 1;
      setTimeout(() => {
        if (!event || !myParticipant) return; // will re-run effect when deps change
      }, 250);
    }
  }, [event, myParticipant, matchFormOpen]);

  // Detect existing match form submission
  useEffect(() => {
    async function checkExisting() {
      if (user && eventId && event && ['in-progress', 'matching', 'finished'].includes(event.mode)) {
        const mDoc = await getDoc(doc(db, `matches/${eventId}_${user.uid}`));
        setHasPreviousMatchForm(mDoc.exists());
      } else {
        setHasPreviousMatchForm(false);
      }
    }
    checkExisting();
  }, [user, eventId, event, matchSubmitted]);

  // Signup handler
  const handleSignup = async () => {
    setSignupError('');
    setSignupSuccess('');
    if (!user || !eventId || !profile) {
      setSignupError('You must be logged in and have a complete profile.');
      return;
    }
    if (role === 'admin') {
      setSignupError('Admins cannot join events.');
      return;
    }
    if (!allSignupFieldsValid) {
      setSignupError('Please complete all required fields and accept the user agreement.');
      return;
    }
    try {
      const answersWithDefaults = { ...signupAnswers, ticketType: 'premium' };
      const q = query(
        fbCollection(db, 'participants'),
        where('eventId', '==', eventId),
        where('userId', '==', user.uid)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        setSignupError('You are already signed up for this event.');
        return;
      }
      const newRef = await addDoc(fbCollection(db, 'participants'), {
        eventId,
        userId: user.uid,
        gender: (profile.gender || '').toLowerCase(),
        status: 'pending',
        answers: answersWithDefaults,
        createdAt: serverTimestamp(),
      });
      setSignupSuccess('Signed up!');
      setShowSignup(false);
      setMyParticipant({ id: newRef.id, eventId, userId: user.uid, gender: (profile.gender || '').toLowerCase(), status: 'pending', answers: answersWithDefaults });
    } catch (e) {
      setSignupError('Failed to sign up. Please try again.');
    }
  };

  const openSeverDialog = (target) => {
    setSeverSubmitError('');
    setSeverReason('');
    setSeverTarget(target);
    setSeverDialogOpen(true);
  };

  const closeSeverDialog = () => {
    setSeverDialogOpen(false);
    setSeverTarget(null);
    setSeverReason('');
    setSeverSubmitError('');
  };

  const submitSever = async () => {
    if (!user || !eventId || !severTarget?.uid) return;
    setSeverSubmitting(true);
    setSeverSubmitError('');
    setSeveringIds(prev => Array.from(new Set([...(prev||[]), severTarget.uid])));
    try {
      const token = await user.getIdToken();
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
      const resp = await fetch(`${backendUrl}/api/match/event/${eventId}/mutual/sever`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ otherUserId: severTarget.uid, reason: severReason })
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.message || json?.meta?.reason || `HTTP ${resp.status}`);
      // Optimistic update
      setMutualMatches(prev => (prev || []).map(p => {
        const id = p.uid || p.id;
        if (id === severTarget.uid) {
          return { ...p, severed: true, severedBy: user.uid, severedAt: new Date().toISOString(), severedReason: severReason };
        }
        return p;
      }));
      closeSeverDialog();
    } catch (e) {
      setSeverSubmitError(e.message || 'Failed to sever match');
    } finally {
      setSeveringIds(prev => (prev || []).filter(id => id !== severTarget?.uid));
      setSeverSubmitting(false);
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 10 }} />;
  if (!event) return <Typography>Event not found.</Typography>;

  return (
    <Box sx={{
      maxWidth: 700,
      mx: 'auto',
      mt: { xs: 1, sm: 2, md: 4 },
      p: { xs: 1, sm: 2, md: 3 },
      width: '100%',
      minHeight: '100vh',
      boxSizing: 'border-box',
      background: { xs: '#f9f9f9', sm: 'none' }
    }}>
      <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: { xs: 0, sm: 2 }, boxShadow: { xs: 0, sm: 2 } }}>
  {/* diagnostics removed */}

        {/* Event title + inline status chip for participant */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>{event.name}</Typography>
          {myParticipant && (
            <Chip
              size="small"
              label={getParticipantStatusLabel(myParticipant.status)}
              color={myParticipant.status === 'approved_paid' ? 'success' : myParticipant.status === 'cancelled' ? 'default' : 'warning'}
              sx={{ height: 28 }}
            />
          )}
        </Box>
        {event.imageUrl && (
          <Box sx={{ mb: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
            <img src={event.imageUrl} alt="Event" style={{ width: '100%', maxWidth: 400, height: 'auto', maxHeight: 220, borderRadius: 8, objectFit: 'cover', margin: '0 auto', display: 'block' }} />
          </Box>
        )}
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Date: {(() => {
          if (!event?.date) return '';
          let d;
          if (event.date?.seconds) d = new Date(event.date.seconds * 1000);
          else if (typeof event.date?.toDate === 'function') d = event.date.toDate();
          else d = new Date(event.date);
          return isNaN(d) ? '' : d.toLocaleDateString();
        })()}</Typography>
        {(() => {
          const isApprovedPaid = myParticipant && myParticipant.status === 'approved_paid';
          const visibleAddress = isApprovedPaid ? (event.specificLocation || event.generalLocation || '') : (event.generalLocation || '');
          return (
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Location: {visibleAddress || ''}
              {visibleAddress && (
                <IconButton
                  size="small"
                  sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}
                  component="a"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(visibleAddress)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open in Google Maps"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="#4285F4" style={{ marginRight: 4 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                  <span style={{ fontWeight: 500, color: '#4285F4', fontSize: 15 }}>Map it</span>
                </IconButton>
              )}
            </Typography>
          );
        })()}
        {event.summary && <Typography sx={{ mb: 2 }}>{event.summary}</Typography>}

    {/* Signup section (hidden when user forced open match form)
      Status chip moved inline with title; remove large gray status box */}
  {!matchFormOpen && !urlWantsMatch.current && (() => {
          const userGender = (profile?.gender || '').toLowerCase().trim();
          const limit = (signupLimit || '').toLowerCase().trim();
      // Admins cannot join events
      if (role === 'admin') return null;
      // If event has moved past signups, hide all signup/edit UI entirely
      if (['in-progress', 'matching', 'finished'].includes(mode)) return null;
          if (myParticipant) {
      // Status shown inline above; no extra gray block
      return null;
          } else if (mode === 'signups' || (mode === 'limited-signups' && limit === userGender)) {
            return (
              <Box sx={{ mb: 2 }}>
                <Button variant="contained" onClick={() => setShowSignup(true)}>Sign Up For Event</Button>
                {signupSuccess && <Alert severity="success" sx={{ mt: 2 }}>{signupSuccess}</Alert>}
                {signupError && <Alert severity="error" sx={{ mt: 2 }}>{signupError}</Alert>}
                {showSignup && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <EventSignupQuestions
                      values={signupAnswers}
                      onChange={e => {
                        const { name, value, type, checked } = e.target;
                        setSignupAnswers(a => ({ ...a, [name]: type === 'checkbox' ? checked : value }));
                      }}
                      onBlur={e => {
                        const { name } = e.target;
                        if (name) setSignupTouched(t => ({ ...t, [name]: true }));
                      }}
                      errors={signupErrors}
                      touched={signupTouched}
                    />
                    <Button variant="contained" sx={{ mt: 2 }} onClick={handleSignup} disabled={role === 'admin' || !allSignupFieldsValid}>Submit</Button>
                    <Button sx={{ mt: 2, ml: 2 }} onClick={() => setShowSignup(false)}>Cancel</Button>
                  </Box>
                )}
              </Box>
            );
          } else if (mode === 'limited-signups' && limit === 'none') {
            return <Alert severity="info">Sign-ups are currently closed for this event.</Alert>;
          } else if (mode === 'limited-signups' && (limit === 'male' || limit === 'female')) {
            return <Alert severity="info">Only {limit} sign-ups are allowed at this time.</Alert>;
          } else {
            return <Button variant="outlined" size="small" sx={{ mb: 2 }} disabled>Sign-ups Closed</Button>;
          }
        })()}

        {/* MATCH FORM SECTION */}
        {(() => {
          const canShow = mode === 'in-progress' && myParticipant && myParticipant.status === 'approved_paid';
          if (!canShow && matchFormOpen && !matchSubmitted && mode === 'in-progress') {
            return (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Match form requested but conditions not met. Conditions: mode=in-progress? {String(mode === 'in-progress')} | hasMyParticipant? {String(!!myParticipant)} | status=approved_paid? {String(myParticipant?.status === 'approved_paid')}.
                <Button size="small" sx={{ ml: 2 }} onClick={openMatchForm}>Retry Load</Button>
              </Alert>
            );
          }
          if (!canShow) return null;
          return (
            <Box sx={{ mb: 2 }} ref={matchFormRef}>
              <Alert severity="info">The event is currently in progress! You can access your match form below.</Alert>
              <Box sx={{ height: 24 }} />
              {!matchFormOpen && (
                <Button variant="contained" onClick={openMatchForm}>
                  {hasPreviousMatchForm ? 'Update Match Form' : 'Submit Match Form'}
                </Button>
              )}
              {matchFormOpen && !matchSubmitted && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#eef7ff', borderRadius: 2, border: '1px solid #c5e1ff' }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Who are you interested in?</Typography>
                  {matchFetchError && <Alert severity="error" sx={{ mb: 2 }}>{matchFetchError}</Alert>}
                  {(() => {
                    if (!event || !profile) return null;
                    const myGender = (profile.gender || '').toLowerCase().trim();
                    const oppositeGender = myGender === 'male' ? 'female' : myGender === 'female' ? 'male' : null;
                    if (matchLoading) return <Typography>Loading potential matches...</Typography>;
                    if (!oppositeGender) return <Typography>No eligible participants found.</Typography>;
                    // Build opposite-gender list. Backend already filtered to opposite for regular user, but keep client guard.
                    const backendSupportsStatus = !!(matchApiMeta && (typeof matchApiMeta.approvedPaidParticipants === 'number' || matchApiMeta.statusCounts));
                    const baseOpp = participantProfiles.filter(p => {
                      const gender = (p.gender || '').toLowerCase().trim();
                      if ((p.uid || p.id) === user.uid) return false;
                      return gender === oppositeGender;
                    });
                    let oppParts = baseOpp.filter(p => (p.status || '').toLowerCase() === 'approved_paid');
                    // If eventParticipants only has current user (length 0 or 1), treat backend profiles as authoritative;
                    // else, intersect with non-cancelled participants for safety.
                    if (eventParticipants.length > 1) {
                      const activeSet = new Set(eventParticipants.filter(ep => ep.status !== 'cancelled').map(ep => ep.userId));
                      oppParts = oppParts.filter(p => activeSet.has(p.uid || p.id));
                    }
                    // diagnostics removed
                    return (
                      <>
                        <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>{oppParts.length} potential match{oppParts.length === 1 ? '' : 'es'} found.</Typography>
                        <Box>
                          {oppParts.length === 0 && <Typography>No eligible participants found.</Typography>}
                          {oppParts.map((p, i) => {
                            const displayName = formatDisplayName(p);
                            const pic = getProfileImage(p);
                            const isChecked = matchSelections.includes(p.uid || p.id);
                            const ageVal = computeAge(p);
                            const occupation = getOccupation(p);
                            return (
                              <Box key={p.uid || p.id || i} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 2, sm: 3 }, p: { xs: 1, sm: 2 }, bgcolor: '#fff', borderRadius: 3, boxShadow: 2, minHeight: { xs: 0, sm: 160 }, width: '100%', gap: { xs: 2, sm: 3 } }}>
                                <Box sx={{ minWidth: { xs: 80, sm: 120 }, minHeight: { xs: 80, sm: 120 }, width: { xs: 80, sm: 120 }, height: { xs: 80, sm: 120 }, borderRadius: '50%', overflow: 'hidden', boxShadow: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 1, sm: 0 }, border: '3px solid #1976d2', background: '#f0f0f0', alignSelf: { xs: 'center', sm: 'auto' } }}>
                                  <img src={pic} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => (e.target.style.display = 'none')} />
                                </Box>
                                <Box sx={{ flex: 1, width: '100%' }}>
                                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 20 }, mb: 1 }}>{displayName}</Typography>
                                  <Typography variant="body1" sx={{ color: '#666', fontSize: { xs: 14, sm: 16 } }}>Age: {ageVal != null ? ageVal : 'N/A'}</Typography>
                                  <Typography variant="body1" sx={{ color: '#666', fontSize: { xs: 14, sm: 16 } }}>Occupation: {occupation || 'N/A'}</Typography>
                                </Box>
                                <Button variant={isChecked ? 'contained' : 'outlined'} color={isChecked ? 'success' : 'primary'} size="large" sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 2, sm: 0 }, minWidth: { xs: '100%', sm: 120 }, fontSize: { xs: 14, sm: 16 } }} onClick={() => setMatchSelections(sel => isChecked ? sel.filter(id => id !== (p.uid || p.id)) : [...sel, p.uid || p.id])}>{isChecked ? 'Interested' : 'Select'}</Button>
                              </Box>
                            );
                          })}
                          <Button variant="contained" sx={{ mt: 2 }} onClick={async () => {
                            if (!user || !eventId) return;
                            try {
                              await setDoc(doc(db, `matches/${eventId}_${user.uid}`), {
                                selections: matchSelections,
                                userId: user.uid,
                                eventId,
                                age: profile?.dob ? computeAge(profile) : null,
                                occupation: profile?.occupation || profile?.profile?.occupation || '',
                                submittedAt: serverTimestamp(),
                              });
                            } catch (e) {}
                            setHasPreviousMatchForm(true);
                            setMatchSubmitted(s => !s);
                            setMatchFormOpen(false);
                          }}>Submit Match Form</Button>
                          <Button sx={{ mt: 2, ml: 2 }} onClick={() => setMatchFormOpen(false)}>Cancel</Button>
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              )}
              {matchSubmitted && <Alert severity="success" sx={{ mt: 2 }}>Match form submitted!</Alert>}
            </Box>
          );
        })()}

        {mode === 'matching' && <Alert severity="info" sx={{ mb: 2 }}>Matching is in progress. No more match forms can be submitted.</Alert>}
        {mode === 'finished' && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="success">Matching is complete! Your mutual matches are below.</Alert>
            {lateMatchPrompt && <Alert severity="success" sx={{ mt: 2 }}>{lateMatchPrompt}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button size="small" variant="outlined" onClick={async () => {
                if (!user || !eventId) return;
                setMutualLoading(true);
                setMutualError('');
                try {
                  const token = await user.getIdToken();
                  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
                  const resp = await fetch(`${backendUrl}/api/match/event/${eventId}/mutual`, { headers: { Authorization: `Bearer ${token}` } });
                  const json = await resp.json().catch(() => ({}));
                  if (!resp.ok) throw new Error(json?.meta?.reason || json?.message || `HTTP ${resp.status}`);
                  const arr = json.matches || json.participants || [];
                  setMutualMatches(Array.isArray(arr) ? arr : []);
                  const liked = json.likedByOnly || [];
                  setLikedByOnly(Array.isArray(liked) ? liked : []);
                  const interested = json.interestedInYou || liked;
                  setInterestedInYou(Array.isArray(interested) ? interested : []);
                } catch (e) {
                  setMutualError(e.message || 'Failed to refresh matches');
                } finally {
                  setMutualLoading(false);
                }
              }}>Refresh</Button>
            </Box>
            {mutualLoading && <Box sx={{ mt: 2 }}><CircularProgress size={20} /> <Typography component="span" sx={{ ml: 1 }}>Loading mutual matches…</Typography></Box>}
            {mutualError && <Alert severity="error" sx={{ mt: 2 }}>{mutualError}</Alert>}
            {!mutualLoading && !mutualError && (
              <Box sx={{ mt: 2 }}>
                {mutualMatches.length === 0 && (
                  <Typography>No mutual matches at this time.</Typography>
                )}
                {mutualMatches.map((p, i) => {
                  const displayName = formatDisplayName(p);
                  const pic = getProfileImage(p);
                  const ageVal = computeAge(p);
                  const occupation = getOccupation(p);
                  const otherId = p.uid || p.id;
                  const isSevered = !!(p.severed || p.severedAt);
                  const severedByYou = isSevered && (p.severedBy === user?.uid);
                  const fullName = p.name || p.displayName || p.fullName || displayName;
                  const phone = p.phone || p.phoneNumber || p?.profile?.phone || p?.profile?.phoneNumber || '';
                   return (
                     <Box key={p.uid || p.id || i} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 2, sm: 3 }, p: { xs: 1, sm: 2 }, bgcolor: '#fff', borderRadius: 3, boxShadow: 2, minHeight: { xs: 0, sm: 160 }, width: '100%', gap: { xs: 2, sm: 3 } }}>
                       <Box sx={{ minWidth: { xs: 80, sm: 120 }, minHeight: { xs: 80, sm: 120 }, width: { xs: 80, sm: 120 }, height: { xs: 80, sm: 120 }, borderRadius: '50%', overflow: 'hidden', boxShadow: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 1, sm: 0 }, border: '3px solid #2e7d32', background: '#f0f0f0', alignSelf: { xs: 'center', sm: 'auto' } }}>
                         <img src={pic} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => (e.target.style.display = 'none')} />
                       </Box>
                       <Box sx={{ flex: 1, width: '100%' }}>
                         <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 20 }, mb: 1 }}>{fullName}</Typography>
                         <Typography variant="body1" sx={{ color: '#666', fontSize: { xs: 14, sm: 16 } }}>Age: {ageVal != null ? ageVal : 'N/A'}</Typography>
                        <Typography variant="body1" sx={{ color: '#666', fontSize: { xs: 14, sm: 16 } }}>Occupation: {occupation || 'N/A'}</Typography>
                        {phone && (
                          <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: { xs: 14, sm: 16 } }}>Phone: {formatPhoneNumber(phone)}</Typography>
                        )}
                         {isSevered ? (
                           <Box sx={{ mt: 1 }}>
                             <Chip size="small" color="warning" label={`Match severed${severedByYou ? ' by you' : ''}`} />
                             {p.severedAt && (
                               <Typography variant="caption" sx={{ display: 'block', color: '#a66', mt: 0.5 }}>
                                 {`Severed at: ${new Date(p.severedAt && typeof p.severedAt === 'string' ? p.severedAt : (p.severedAt?.seconds ? p.severedAt.seconds * 1000 : p.severedAt)).toLocaleString()}`}
                               </Typography>
                             )}
                           </Box>
                         ) : (
                           <Button
                             variant="outlined"
                             color="error"
                             size="small"
                             sx={{ mt: 1 }}
                             disabled={severingIds.includes(otherId)}
                             onClick={() => openSeverDialog({ uid: otherId, name: displayName })}
                           >
                             {severingIds.includes(otherId) ? 'Unmatching…' : 'Problem with this match? Tap or click to unmatch.'}
                           </Button>
                         )}
                       </Box>
                     </Box>
                   );
                 })}
              </Box>
            )}
            {!mutualLoading && !mutualError && (() => {
              const matchedIds = new Set((mutualMatches || []).map(m => m.uid || m.id));
              const actionableInterested = (interestedInYou || []).filter((p) => {
                const id = p.uid || p.id;
                return id && p.canMatch !== false && !matchedIds.has(id);
              });
              if (!actionableInterested.length) return null;

              return (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>People interested in you</Typography>
                  <Typography variant="body2" sx={{ color: '#6d4c41', mb: 1 }}>
                    Are you interested? Click "match now" to make a new match, then proceed with sending them a text.
                  </Typography>
                  {actionableInterested.map((p, i) => {
                    const displayName = formatDisplayName(p);
                    const pic = getProfileImage(p);
                    const ageVal = computeAge(p);
                    const occupation = getOccupation(p);
                    const otherId = p.uid || p.id;
                    return (
                      <Box key={otherId || i} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 2, sm: 3 }, p: { xs: 1, sm: 2 }, bgcolor: '#fff', borderRadius: 3, boxShadow: 2, minHeight: { xs: 0, sm: 140 }, width: '100%', gap: { xs: 2, sm: 3 } }}>
                        <Box sx={{ minWidth: { xs: 70, sm: 100 }, minHeight: { xs: 70, sm: 100 }, width: { xs: 70, sm: 100 }, height: { xs: 70, sm: 100 }, borderRadius: '50%', overflow: 'hidden', boxShadow: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 1, sm: 0 }, border: '3px solid #ffb300', background: '#f0f0f0', alignSelf: { xs: 'center', sm: 'auto' } }}>
                          <img src={pic} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => (e.target.style.display = 'none')} />
                        </Box>
                        <Box sx={{ flex: 1, width: '100%' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 20 }, mb: 1 }}>{displayName}</Typography>
                          <Typography variant="body1" sx={{ color: '#666', fontSize: { xs: 14, sm: 16 } }}>Age: {ageVal != null ? ageVal : 'N/A'}</Typography>
                          <Typography variant="body1" sx={{ color: '#666', fontSize: { xs: 14, sm: 16 } }}>Occupation: {occupation || 'N/A'}</Typography>
                        </Box>
                        <Button
                          variant="contained"
                          color="warning"
                          size="medium"
                          onClick={async () => {
                            if (!user || !eventId || !otherId) return;
                            try {
                              const token = await user.getIdToken();
                              const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
                              const resp = await fetch(`${backendUrl}/api/match/event/${eventId}/like`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ otherUserId: otherId })
                              });
                              const json = await resp.json().catch(() => ({}));
                              if (!resp.ok) throw new Error(json?.message || json?.meta?.reason || `HTTP ${resp.status}`);
                              // Refresh lists after like
                              try {
                                const r = await fetch(`${backendUrl}/api/match/event/${eventId}/mutual`, { headers: { Authorization: `Bearer ${token}` } });
                                const j = await r.json().catch(() => ({}));
                                const arr2 = j.matches || j.participants || [];
                                setMutualMatches(Array.isArray(arr2) ? arr2 : []);
                                setLikedByOnly(Array.isArray(j.likedByOnly) ? j.likedByOnly : []);
                                setInterestedInYou(Array.isArray(j.interestedInYou) ? j.interestedInYou : []);
                                const nowMutual = Array.isArray(arr2) && arr2.some(m => (m.uid || m.id) === otherId);
                                if (json?.becameMutual || nowMutual) {
                                  setLateMatchPrompt(`It's a match with ${displayName}. Reach out while it's fresh.`);
                                }
                              } catch (_) { /* ignore refresh error */ }
                            } catch (e) {
                              setMutualError(e.message || 'Failed to mark interested');
                            }
                          }}
                        >
                          Match now
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </Box>
        )}
      </Paper>

      {/* Sever Match Dialog */}
      <Dialog open={severDialogOpen} onClose={closeSeverDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Is there a problem with this match?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography>Are you sure you want to unmatch with {severTarget?.name || 'this participant'}? THIS CANNOT BE UNDONE.</Typography>
          <TextField
            label="Reason (optional)"
            value={severReason}
            onChange={(e) => setSeverReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          {severSubmitError && <Alert severity="error">{severSubmitError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSeverDialog} disabled={severSubmitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitSever} disabled={severSubmitting}>
            {severSubmitting ? 'Unmatching…' : 'Unmatch'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MainEventPage;
